import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  Card,
  CardContent,
  CardActions,
  Grid,
  Avatar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Block, 
  CheckCircle, 
  HourglassEmpty, 
  Person, 
  PersonAdd, 
  Group, 
  Notifications,
  Check,
  Email,
  Event,
  Article,
  Gavel as GavelIcon,
  PersonRemove as PersonRemoveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Individual error states for better error handling
  const [usersError, setUsersError] = useState(null);
  const [pendingUsersError, setPendingUsersError] = useState(null);
  
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Clear previous errors
      setError(null);
      setUsersError(null);
      setPendingUsersError(null);
      
      console.log('Fetching admin data with resilient error handling...');
      
      // Use Promise.allSettled to handle individual API failures gracefully
      const [usersResult, pendingUsersResult] = await Promise.allSettled([
        adminService.getUsers(),
        adminService.getPendingUsers()
      ]);
      
      // Handle users data
      if (usersResult.status === 'fulfilled') {
        console.log('Users API Response:', usersResult.value);
        const usersArray = usersResult.value.data?.data?.users || [];
        console.log('Users array length:', usersArray.length);
        setUsers(usersArray);
      } else {
        console.error('Failed to load users:', usersResult.reason);
        setUsersError('Failed to load users. Please try refreshing the page.');
        setUsers([]); // Set empty array so UI can still render
      }
      
      // Handle pending users data
      if (pendingUsersResult.status === 'fulfilled') {
        console.log('Pending Users API Response:', pendingUsersResult.value);
        const pendingUsersArray = pendingUsersResult.value.data?.data?.users || [];
        console.log('Pending users array length:', pendingUsersArray.length);
        setPendingUsers(pendingUsersArray);
      } else {
        console.error('Failed to load pending users:', pendingUsersResult.reason);
        setPendingUsersError('Failed to load pending users. This section may be temporarily unavailable.');
        setPendingUsers([]); // Set empty array so UI can still render
      }
      
      // Check if all APIs failed
      const allFailed = usersResult.status === 'rejected' && 
                       pendingUsersResult.status === 'rejected';
      
      if (allFailed) {
        setError('Unable to load admin data. Please check your connection and try again.');
      }
      
    } catch (err) {
      console.error('Unexpected error in fetchData:', err);
      setError('An unexpected error occurred while loading admin data.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await adminService.deleteUser(userId);
        setUsers(users.filter(u => u.user_id !== userId));
      } catch (err) {
        setError('Failed to delete user');
      }
    }
  };

  const handleEditUser = (user) => {
    setEditDialog({ open: true, user: { ...user } });
  };

  const handleSaveUser = async () => {
    try {
      await adminService.updateUser(editDialog.user.user_id, editDialog.user);
      setUsers(users.map(u => 
        u.user_id === editDialog.user.user_id ? editDialog.user : u
      ));
      setEditDialog({ open: false, user: null });
    } catch (err) {
      setError('Failed to update user');
    }
  };

  // New pending user management handlers
  const handleApproveUser = async (userId) => {
    try {
      setActionLoading(true);
      await adminService.approveUser(userId);
      
      // Move user from pending to approved users
      const approvedUser = pendingUsers.find(u => u.user_id === userId);
      if (approvedUser) {
        setPendingUsers(pendingUsers.filter(u => u.user_id !== userId));
        setUsers([...users, { ...approvedUser, role: 'user' }]);
      }
      
      setSnackbar({
        open: true,
        message: 'User approved successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to approve user',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId) => {
    if (window.confirm('Are you sure you want to suspend this user?')) {
      try {
        setActionLoading(true);
        await adminService.suspendUser(userId);
        
        // Move user from approved to pending
        const suspendedUser = users.find(u => u.user_id === userId);
        if (suspendedUser) {
          setUsers(users.filter(u => u.user_id !== userId));
          setPendingUsers([...pendingUsers, { ...suspendedUser, role: 'no_role' }]);
        }
        
        setSnackbar({
          open: true,
          message: 'User suspended successfully!',
          severity: 'success'
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: 'Failed to suspend user',
          severity: 'error'
        });
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      setActionLoading(true);
      await adminService.bulkApproveUsers(selectedUsers);
      
      // Move selected users from pending to approved
      const approvedUsers = pendingUsers.filter(u => selectedUsers.includes(u.user_id));
      setPendingUsers(pendingUsers.filter(u => !selectedUsers.includes(u.user_id)));
      setUsers([...users, ...approvedUsers.map(u => ({ ...u, role: 'user' }))]);
      setSelectedUsers([]);
      
      setSnackbar({
        open: true,
        message: `${selectedUsers.length} users approved successfully!`,
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to bulk approve users',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === pendingUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(pendingUsers.map(u => u.user_id));
    }
  };

  // Judge management functions
  const handleMakeJudge = async (userId) => {
    try {
      await adminService.makeUserJudge(userId);
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, role: 'judge' } : u
      ));
      setSnackbar({
        open: true,
        message: 'User successfully made a judge!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error making user judge:', err);
      setSnackbar({
        open: true,
        message: 'Failed to make user a judge',
        severity: 'error'
      });
    }
  };

  const handleRemoveJudge = async (userId) => {
    try {
      await adminService.removeJudgeRole(userId);
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, role: 'user' } : u
      ));
      setSnackbar({
        open: true,
        message: 'Judge role successfully removed!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error removing judge role:', err);
      setSnackbar({
        open: true,
        message: 'Failed to remove judge role',
        severity: 'error'
      });
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">Please log in to access user management.</Alert>
      </Container>
    );
  }

  if (user.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Access denied. Admin privileges required.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Header with Quick Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Users
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Article />}
            onClick={() => navigate('/admin/stories')}
          >
            Manage Stories
          </Button>
          <Button
            variant="outlined"
            startIcon={<Event />}
            onClick={() => navigate('/admin/events')}
          >
            Manage Events
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            label={
              <Badge badgeContent={pendingUsers.length} color="error" max={99}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HourglassEmpty fontSize="small" />
                  Pending Users ({pendingUsers.length})
                </Box>
              </Badge>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group fontSize="small" />
                All Users ({users.length})
              </Box>
            } 
          />
          {/* 
            Events Tab - Commented out to reduce redundancy with "Manage Events" button
            Uncomment if we need dedicated Events tab with statistics/analytics in the future
            
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Event fontSize="small" />
                  Events
                </Box>
              } 
            />
          */}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Pending Users Tab */}
          {tabValue === 0 && (
            <Box>
              {/* Header with bulk actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Notifications color="warning" />
                  Users Awaiting Approval
                </Typography>
                {pendingUsers.length > 0 && !pendingUsersError && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedUsers.length === pendingUsers.length && pendingUsers.length > 0}
                          indeterminate={selectedUsers.length > 0 && selectedUsers.length < pendingUsers.length}
                          onChange={handleSelectAll}
                        />
                      }
                      label="Select All"
                    />
                    <Button
                      variant="contained"
                      startIcon={<PersonAdd />}
                      onClick={handleBulkApprove}
                      disabled={selectedUsers.length === 0 || actionLoading}
                      color="success"
                    >
                      Approve Selected ({selectedUsers.length})
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Show error if pending users failed to load */}
              {pendingUsersError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {pendingUsersError}
                  <Button 
                    size="small" 
                    onClick={fetchData} 
                    sx={{ ml: 2 }}
                    variant="outlined"
                  >
                    Retry
                  </Button>
                </Alert>
              )}

              {!pendingUsersError && pendingUsers.length === 0 ? (
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ mr: 1 }} />
                  No users pending approval. All users have been processed!
                </Alert>
              ) : !pendingUsersError && (
                <Grid container spacing={3}>
                  {pendingUsers.map((pendingUser) => (
                    <Grid item xs={12} md={6} lg={4} key={pendingUser.user_id}>
                      <Card 
                        elevation={2}
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          border: selectedUsers.includes(pendingUser.user_id) ? '2px solid' : '1px solid',
                          borderColor: selectedUsers.includes(pendingUser.user_id) ? 'primary.main' : 'grey.300',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Checkbox
                              checked={selectedUsers.includes(pendingUser.user_id)}
                              onChange={() => handleSelectUser(pendingUser.user_id)}
                              sx={{ mr: 1 }}
                            />
                            <Avatar sx={{ width: 48, height: 48, mr: 2, bgcolor: 'warning.main' }}>
                              <Person />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" noWrap>
                                {pendingUser.given_name && pendingUser.family_name 
                                  ? `${pendingUser.given_name} ${pendingUser.family_name}`.trim()
                                  : pendingUser.name || 'No Name'
                                }
                              </Typography>
                              <Chip 
                                label="Pending Approval" 
                                color="warning" 
                                size="small"
                                icon={<HourglassEmpty />}
                              />
                            </Box>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Email:</strong> {pendingUser.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Signed up:</strong> {
                                pendingUser.created_at 
                                  ? new Date(pendingUser.created_at).toLocaleDateString()
                                  : 'Unknown'
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>User ID:</strong> {pendingUser.user_id?.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </CardContent>
                        
                        <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<Check />}
                            onClick={() => handleApproveUser(pendingUser.user_id)}
                            disabled={actionLoading}
                            size="small"
                          >
                            Approve
                          </Button>
                          <Tooltip title="Send email to user">
                            <IconButton
                              size="small"
                              onClick={() => window.location.href = `mailto:${pendingUser.email}?subject=Account Status Update`}
                            >
                              <Email />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* All Users Tab */}
          {tabValue === 1 && (
            <Box>
              {/* Show error if users failed to load */}
              {usersError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {usersError}
                  <Button 
                    size="small" 
                    onClick={fetchData} 
                    sx={{ ml: 2 }}
                    variant="outlined"
                  >
                    Retry
                  </Button>
                </Alert>
              )}

              {!usersError && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              No users found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow key={user.user_id}>
                            <TableCell>
                              {user.given_name && user.family_name 
                                ? `${user.given_name} ${user.family_name}`.trim()
                                : user.name || user.email || 'No Name'
                              }
                            </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={
                            user.role === 'admin' ? 'primary' : 
                            user.role === 'judge' ? 'secondary' : 
                            'success'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.created_at 
                          ? new Date(user.created_at).toLocaleDateString()
                          : 'No Date'
                        }
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        {/* Judge Management Buttons */}
                        {user.role === 'user' && (
                          <Tooltip title="Make Judge">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleMakeJudge(user.user_id)}
                            >
                              <GavelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {user.role === 'judge' && (
                          <Tooltip title="Remove Judge Role">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRemoveJudge(user.user_id)}
                            >
                              <PersonRemoveIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Suspend User">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleSuspendUser(user.user_id)}
                            disabled={user.role === 'admin'} // Don't allow suspending admins
                          >
                            <Block />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUser(user.user_id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* 
            Events Tab Content - Commented out to reduce redundancy with "Manage Events" button
            This tab provided a dedicated Events management interface within the admin panel.
            
            Uncomment if we need:
            - Event statistics and analytics within admin panel
            - Dedicated events dashboard with charts/metrics
            - Quick event actions without leaving admin panel
            
            Note: The "Manage Events" button in Overview tab provides direct access to full event management
            
          {tabValue === 3 && (
            <Box>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Event sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Event Management
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Create, manage, and monitor writing events and competitions
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Event />}
                  onClick={() => navigate('/admin/events')}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Go to Event Management
                </Button>
              </Box>
            </Box>
          )}
          */}
        </Box>
      </Paper>

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, user: null })}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Given Name"
            value={editDialog.user?.given_name || ''}
            onChange={(e) => setEditDialog(prev => ({
              ...prev,
              user: { ...prev.user, given_name: e.target.value }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Family Name"
            value={editDialog.user?.family_name || ''}
            onChange={(e) => setEditDialog(prev => ({
              ...prev,
              user: { ...prev.user, family_name: e.target.value }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            value={editDialog.user?.email || ''}
            onChange={(e) => setEditDialog(prev => ({
              ...prev,
              user: { ...prev.user, email: e.target.value }
            }))}
            margin="normal"
            type="email"
          />
          <TextField
            fullWidth
            label="Role"
            select
            value={editDialog.user?.role || 'user'}
            onChange={(e) => setEditDialog(prev => ({
              ...prev,
              user: { ...prev.user, role: e.target.value }
            }))}
            margin="normal"
            SelectProps={{
              native: true,
            }}
          >
            <option value="no_role">Pending Approval</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveUser} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPanel;
